/**
 * NIL Go integration package (US-033.6).
 *
 * Single internal interface (`NilGoSubmissionProvider`) so the live NIL Go
 * partner API can ship the moment it opens — no backtracking on a fake
 * "integration" claim. Provider is selected by `NIL_GO_PROVIDER` env var.
 *
 * Three adapters in v1:
 *   - export_only (default) — writes a JSON+CSV bundle; the athlete
 *     uploads to NIL Go themselves. The export is genuinely useful and
 *     the only honest UX while NIL Go has no partner API.
 *   - mock                  — deterministic responses, for tests.
 *   - api                   — reserved; throws on use until configured
 *     with the real NIL Go endpoint contract.
 *
 * The provider has zero leakage outside this package: routers and UI
 * only ever import from `@app/nil-go`, never reach into adapters.
 *
 * @package @app/nil-go
 */

import type { NilGoSubmissionPayload } from '@app/validators';

export type ProviderId = 'export_only' | 'mock' | 'api';

export interface SubmitResult {
  /** Stable identifier for the submission attempt — used as the row's
   * `externalSubmissionId` going forward. For `export_only`, a synthetic
   * `export-<random>` id since there's no remote system to assign one. */
  externalSubmissionId: string;
  /** The mutated submission status post-submit. For export_only this is
   * `submitted` (the athlete is presumed to upload the export themselves);
   * for `mock` it's the deterministic next state; for `api` it'll be
   * whatever NIL Go's response indicates. */
  nextStatus: 'submitted' | 'under_review' | 'approved' | 'needs_revision' | 'rejected';
  /** Optional provider-side audit note appended to the row's `auditLog`. */
  auditNote?: string;
  /** For `export_only`: the encoded JSON + CSV bundle the UI then triggers
   * a download for. Provider-shaped so a future api adapter can return
   * undefined. */
  exportBundle?: {
    filename: string;
    jsonContent: string;
    csvContent: string;
  };
}

export interface GetStatusResult {
  status:
    | 'prepared'
    | 'submitted'
    | 'under_review'
    | 'approved'
    | 'needs_revision'
    | 'rejected'
    | 'cancelled';
  /** Free-form reason from NIL Go (or stub from mock); attached to
   * decisionReason. */
  reason?: string;
}

export interface NilGoSubmissionProvider {
  readonly id: ProviderId;
  /** Submit a fully-validated payload. Implementations must not throw on
   * happy paths; return a SubmitResult instead. */
  submit(payload: NilGoSubmissionPayload): Promise<SubmitResult>;
  /** Poll for a remote decision. For export_only this always returns the
   * same status as the last call (no remote system). */
  getStatus(externalSubmissionId: string): Promise<GetStatusResult>;
  /** Cancel an outstanding submission. Idempotent. */
  cancel(externalSubmissionId: string): Promise<void>;
}

// ---------------------------------------------------------------------------

class ExportOnlyNilGoProvider implements NilGoSubmissionProvider {
  readonly id = 'export_only' as const;

  async submit(payload: NilGoSubmissionPayload): Promise<SubmitResult> {
    const id = `export-${cryptoRandom()}`;
    return {
      externalSubmissionId: id,
      nextStatus: 'submitted',
      auditNote:
        'Export-only provider: athlete is responsible for uploading the export bundle to NIL Go.',
      exportBundle: {
        filename: `nilboard-nilgo-${payload.deal.id}-${stampForFile(payload.submittedAt)}`,
        jsonContent: JSON.stringify(payload, null, 2),
        csvContent: toCsvBundle(payload),
      },
    };
  }

  async getStatus(externalSubmissionId: string): Promise<GetStatusResult> {
    // No remote system; the row's persisted status is the source of truth.
    void externalSubmissionId;
    return { status: 'submitted' };
  }

  async cancel(externalSubmissionId: string): Promise<void> {
    void externalSubmissionId;
    // Nothing to call. The router will mark the row cancelled in the DB.
  }
}

// ---------------------------------------------------------------------------

class MockNilGoProvider implements NilGoSubmissionProvider {
  readonly id = 'mock' as const;

  async submit(payload: NilGoSubmissionPayload): Promise<SubmitResult> {
    // Deterministic: same dealId always lands on the same mock status so
    // tests can pick a "known approved" or "known rejected" fixture.
    const seed = payload.deal.id;
    const status = pickMockStatus(seed);
    return {
      externalSubmissionId: `mock-${seed}`,
      nextStatus: status,
      auditNote: `MockNilGoProvider deterministic decision for seed=${seed}`,
    };
  }

  async getStatus(externalSubmissionId: string): Promise<GetStatusResult> {
    return { status: 'under_review', reason: 'Mock provider: stub status.' };
  }

  async cancel(): Promise<void> {
    /* no-op */
  }
}

// ---------------------------------------------------------------------------

class ApiNilGoProvider implements NilGoSubmissionProvider {
  readonly id = 'api' as const;

  async submit(): Promise<SubmitResult> {
    throw new Error(
      'ApiNilGoProvider is reserved for a future NIL Go partner integration. ' +
        'Set NIL_GO_PROVIDER=export_only (default) or mock until the partner API is wired.',
    );
  }
  async getStatus(): Promise<GetStatusResult> {
    throw new Error('ApiNilGoProvider is reserved.');
  }
  async cancel(): Promise<void> {
    throw new Error('ApiNilGoProvider is reserved.');
  }
}

// ---------------------------------------------------------------------------

/** Provider selector. Reads `NIL_GO_PROVIDER` from env on call; safe to call
 * per-request because providers are stateless. Defaults to `export_only` —
 * the only adapter that's honest by default. */
export function getNilGoProvider(env: NodeJS.ProcessEnv = process.env): NilGoSubmissionProvider {
  const raw = env.NIL_GO_PROVIDER?.toLowerCase();
  switch (raw) {
    case 'mock':
      return new MockNilGoProvider();
    case 'api':
      return new ApiNilGoProvider();
    default:
      return new ExportOnlyNilGoProvider();
  }
}

// Re-export the typed payload so consumers can `import { NilGoSubmissionPayload } from '@app/nil-go'`
// without crossing back into @app/validators.
export type { NilGoSubmissionPayload };

// ---------------------------------------------------------------------------
// Helpers (internal — not exported)

function cryptoRandom(): string {
  // Node 22 has globalThis.crypto.randomUUID; fall back to Math.random for
  // ancient runtimes. Used only to mint unique export-bundle IDs.
  try {
    if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
      return globalThis.crypto.randomUUID().slice(0, 12);
    }
  } catch {
    /* ignore */
  }
  return Math.random().toString(36).slice(2, 14);
}

function stampForFile(iso: string): string {
  // YYYY-MM-DD-HHMMSS, filename-safe.
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}-${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}`;
}

function pickMockStatus(seed: string): SubmitResult['nextStatus'] {
  // Hash-and-pick so mocks are deterministic per seed.
  const hash = Array.from(seed).reduce((acc, c) => (acc * 31 + c.charCodeAt(0)) | 0, 0);
  const bucket = Math.abs(hash) % 5;
  return (['submitted', 'under_review', 'approved', 'needs_revision', 'rejected'] as const)[bucket];
}

function toCsvBundle(payload: NilGoSubmissionPayload): string {
  // Single-row CSV mirroring the key NIL Go fields. Compliance officers
  // open this in Excel; the JSON file carries the full structured shape.
  const fields: Array<[string, string]> = [
    ['submitted_at', payload.submittedAt],
    ['deal_id', payload.deal.id],
    ['deal_title', payload.deal.title],
    ['deal_currency', payload.deal.currency],
    ['deal_total_cash_equivalent', String(payload.deal.totalCashEquivalent)],
    ['deal_start_date', payload.deal.startDate ?? ''],
    ['deal_end_date', payload.deal.endDate ?? ''],
    ['deal_is_exclusive', String(payload.deal.isExclusive)],
    ['athlete_name', payload.athlete.name],
    ['athlete_email', payload.athlete.email],
    ['athlete_ncaa_id', payload.athlete.ncaaId],
    ['athlete_class_year', payload.athlete.classYear ?? ''],
    ['athlete_sport', payload.athlete.sport ?? ''],
    ['payor_legal_name', payload.payor.legalName],
    ['payor_ein', payload.payor.ein ?? ''],
    ['payor_affiliation', payload.payor.affiliation],
    ['payor_validation_status', payload.payor.validationStatus],
    ['activities_count', String(payload.activities.length)],
    ['compensation_items_count', String(payload.compensation.length)],
    ['fmv_low', payload.fmv ? String(payload.fmv.estimatedLow) : ''],
    ['fmv_high', payload.fmv ? String(payload.fmv.estimatedHigh) : ''],
    ['fmv_range_assessment', payload.fmv ? payload.fmv.rangeAssessment : ''],
  ];
  const escape = (s: string) => `"${s.replace(/"/g, '""')}"`;
  const header = fields.map(([k]) => escape(k)).join(',');
  const row = fields.map(([, v]) => escape(v)).join(',');
  return `${header}\n${row}\n`;
}
